import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Users,
  Search,
  Target,
  BookOpen,
  Utensils,
  Activity,
  MessageCircle,
  Send,
  Calendar,
  ArrowLeft,
  Shield,
  Award,
  Clock,
  Edit,
  Trash2,
  Plus,
  Image,
  Video
} from "lucide-react";
import { toast } from "sonner";
import NestedHauoraRings from "../components/NestedHauoraRings";

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("members");
  
  // Members state
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [loadingMemberData, setLoadingMemberData] = useState(false);
  
  // Role/Tier edit state
  const [showEditRoleDialog, setShowEditRoleDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [newTier, setNewTier] = useState("free");
  const [newRole, setNewRole] = useState("user");
  const [savingRole, setSavingRole] = useState(false);
  
  // Feedback state
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    targetType: "goals",
    targetId: "",
    feedbackText: ""
  });
  const [sendingFeedback, setSendingFeedback] = useState(false);
  
  // Scheduled posts state
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);
  const [showNewPostDialog, setShowNewPostDialog] = useState(false);
  const [newPost, setNewPost] = useState({
    text: "",
    imageUrl: "",
    mediaType: null,
    scheduledFor: "",
    status: "scheduled"
  });
  const [creatingPost, setCreatingPost] = useState(false);
  
  // Badges state
  const [badges, setBadges] = useState([]);
  const [memberBadges, setMemberBadges] = useState([]);

  useEffect(() => {
    fetchMembers();
    fetchBadges();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      setFilteredMembers(
        members.filter(m => 
          m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.email?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    } else {
      setFilteredMembers(members);
    }
  }, [searchQuery, members]);
  
  useEffect(() => {
    if (activeTab === "scheduled") {
      fetchScheduledPosts();
    }
  }, [activeTab]);

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API}/admin/members`);
      setMembers(res.data || []);
      setFilteredMembers(res.data || []);
    } catch (err) {
      console.error("Members fetch error:", err);
      toast.error("Failed to load members");
      setMembers([]);
      setFilteredMembers([]);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchBadges = async () => {
    try {
      const res = await axios.get(`${API}/badges`);
      setBadges(res.data || []);
    } catch (err) {
      console.error("Badges fetch error:", err);
    }
  };
  
  const fetchScheduledPosts = async () => {
    setLoadingScheduled(true);
    try {
      const res = await axios.get(`${API}/community/posts/scheduled`);
      setScheduledPosts(res.data || []);
    } catch (err) {
      console.error("Scheduled posts fetch error:", err);
      setScheduledPosts([]);
    } finally {
      setLoadingScheduled(false);
    }
  };

  const selectMember = async (member) => {
    setSelectedMember(member);
    setLoadingMemberData(true);
    setMemberData(null);
    
    try {
      const [pouRes, journalsRes, nutritionRes, activitiesRes, badgesRes] = await Promise.all([
        axios.get(`${API}/admin/member/${member.id}/pou`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/member/${member.id}/journals`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/member/${member.id}/nutrition`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/member/${member.id}/activities`).catch(() => ({ data: [] })),
        axios.get(`${API}/badges/user/${member.id}`).catch(() => ({ data: [] }))
      ]);
      
      setMemberData({
        pou: pouRes.data || [],
        journals: journalsRes.data || [],
        nutrition: nutritionRes.data || [],
        activities: activitiesRes.data || []
      });
      setMemberBadges(badgesRes.data || []);
      
      if (pouRes.data?.length > 0) {
        setFeedbackForm(prev => ({ ...prev, targetId: pouRes.data[0].id }));
      }
    } catch (err) {
      console.error("Member data fetch error:", err);
      toast.error("Failed to load member data");
      setMemberData({ pou: [], journals: [], nutrition: [], activities: [] });
    } finally {
      setLoadingMemberData(false);
    }
  };
  
  const openEditRole = (member, e) => {
    e?.stopPropagation();
    setEditingMember(member);
    setNewTier(member.tier || "free");
    setNewRole(member.role || "user");
    setShowEditRoleDialog(true);
  };
  
  const saveRoleChange = async () => {
    if (!editingMember) return;
    
    setSavingRole(true);
    try {
      await axios.put(`${API}/admin/user/${editingMember.id}/tier`, {
        tier: newTier,
        role: newRole
      });
      toast.success(`Updated ${editingMember.name}'s access`);
      setShowEditRoleDialog(false);
      fetchMembers();
    } catch (err) {
      toast.error("Failed to update access");
    } finally {
      setSavingRole(false);
    }
  };

  const sendFeedback = async () => {
    if (!feedbackForm.feedbackText.trim()) {
      toast.error("Please write your feedback");
      return;
    }
    
    setSendingFeedback(true);
    try {
      await axios.post(`${API}/feedback`, {
        userId: selectedMember.id,
        targetType: feedbackForm.targetType,
        targetId: feedbackForm.targetId || "general",
        feedbackText: feedbackForm.feedbackText
      });
      toast.success("Feedback sent to member");
      setShowFeedbackDialog(false);
      setFeedbackForm({ targetType: "goals", targetId: "", feedbackText: "" });
    } catch (err) {
      toast.error("Failed to send feedback");
    } finally {
      setSendingFeedback(false);
    }
  };
  
  const createScheduledPost = async () => {
    if (!newPost.text.trim()) {
      toast.error("Please write your post");
      return;
    }
    
    setCreatingPost(true);
    try {
      await axios.post(`${API}/community/posts`, {
        text: newPost.text,
        imageUrl: newPost.imageUrl || null,
        mediaType: newPost.mediaType,
        scheduledFor: newPost.scheduledFor || null,
        status: newPost.scheduledFor ? "scheduled" : "published"
      });
      toast.success(newPost.scheduledFor ? "Post scheduled!" : "Post published!");
      setShowNewPostDialog(false);
      setNewPost({ text: "", imageUrl: "", mediaType: null, scheduledFor: "", status: "scheduled" });
      fetchScheduledPosts();
    } catch (err) {
      toast.error("Failed to create post");
    } finally {
      setCreatingPost(false);
    }
  };
  
  const deleteScheduledPost = async (postId) => {
    try {
      await axios.delete(`${API}/community/posts/${postId}`);
      toast.success("Post deleted");
      fetchScheduledPosts();
    } catch (err) {
      toast.error("Failed to delete post");
    }
  };
  
  const awardBadge = async (memberId, badgeId) => {
    try {
      await axios.post(`${API}/badges/award?badgeId=${badgeId}&userId=${memberId}`);
      toast.success("Badge awarded!");
      if (selectedMember) {
        const badgesRes = await axios.get(`${API}/badges/user/${memberId}`);
        setMemberBadges(badgesRes.data || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to award badge");
    }
  };
  
  const revokeBadge = async (userBadgeId) => {
    try {
      await axios.delete(`${API}/badges/revoke/${userBadgeId}`);
      toast.success("Badge revoked");
      if (selectedMember) {
        const badgesRes = await axios.get(`${API}/badges/user/${selectedMember.id}`);
        setMemberBadges(badgesRes.data || []);
      }
    } catch (err) {
      toast.error("Failed to revoke badge");
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'challenger': return 'bg-[#5B8BA0] text-white';
      case 'member': return 'bg-[#3D6B5A] text-white';
      default: return 'bg-[#8FA39B] text-white';
    }
  };
  
  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-[#A8583C] text-white';
      case 'coach': return 'bg-[#D4A84B] text-[#051F17]';
      default: return 'bg-[#0B3D2E]/20 text-[#0B3D2E]';
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-NZ', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0B3D2E]/20 border-t-[#0B3D2E] rounded-full animate-spin"></div>
      </div>
    );
  }

  // Member Detail View
  if (selectedMember) {
    const currentPou = memberData?.pou?.[0];
    
    return (
      <div className="p-4 pb-8" data-testid="member-detail">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setSelectedMember(null)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Avatar className="w-12 h-12 bg-[#0B3D2E]">
            {selectedMember.profilePhoto ? (
              <AvatarImage src={selectedMember.profilePhoto} />
            ) : null}
            <AvatarFallback className="bg-[#0B3D2E] text-white">
              {getInitials(selectedMember.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-[#0B3D2E]">{selectedMember.name}</h1>
            <div className="flex gap-2 mt-1">
              <Badge className={getTierColor(selectedMember.tier)}>
                {selectedMember.tier || 'free'}
              </Badge>
              <Badge className={getRoleColor(selectedMember.role)}>
                {selectedMember.role || 'user'}
              </Badge>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={(e) => openEditRole(selectedMember, e)}
              className="border-[#0B3D2E]/30"
            >
              <Shield className="w-4 h-4" />
            </Button>
            <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#0B3D2E] text-white" size="sm">
                  <MessageCircle className="w-4 h-4 mr-1" /> Feedback
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md mx-4">
                <DialogHeader>
                  <DialogTitle>Send Feedback to {selectedMember.name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Feedback Type</Label>
                    <Select 
                      value={feedbackForm.targetType} 
                      onValueChange={(v) => setFeedbackForm({...feedbackForm, targetType: v})}
                    >
                      <SelectTrigger className="h-12 bg-[#F9F9F7] border-0 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="goals">Goals (My Taha)</SelectItem>
                        <SelectItem value="dailyJournal">Daily Journal</SelectItem>
                        <SelectItem value="nutrition">Nutrition</SelectItem>
                        <SelectItem value="activity">Activity</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Your Feedback</Label>
                    <Textarea
                      placeholder="Write supportive, encouraging feedback..."
                      value={feedbackForm.feedbackText}
                      onChange={(e) => setFeedbackForm({...feedbackForm, feedbackText: e.target.value})}
                      className="min-h-[120px] bg-[#F9F9F7] border-0 rounded-xl"
                    />
                  </div>
                  <Button 
                    onClick={sendFeedback}
                    disabled={sendingFeedback}
                    className="w-full h-12 bg-[#0B3D2E] text-white rounded-full"
                  >
                    {sendingFeedback ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <><Send className="w-4 h-4 mr-1" /> Send Feedback</>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loadingMemberData ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-2 border-[#0B3D2E]/20 border-t-[#0B3D2E] rounded-full animate-spin"></div>
          </div>
        ) : (
          <Tabs defaultValue="overview">
            <TabsList className="grid w-full grid-cols-5 mb-4">
              <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
              <TabsTrigger value="journals" className="text-xs">Journals</TabsTrigger>
              <TabsTrigger value="nutrition" className="text-xs">Nutrition</TabsTrigger>
              <TabsTrigger value="activities" className="text-xs">Activities</TabsTrigger>
              <TabsTrigger value="badges" className="text-xs">Badges</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              {/* Hauora Rings */}
              <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-brand">Hauora Progress</CardTitle>
                </CardHeader>
                <CardContent className="flex justify-center py-4">
                  <NestedHauoraRings
                    wairuaCompleted={currentPou?.wairuaCompleted}
                    hinengaroCompleted={currentPou?.hinengaroCompleted}
                    tinanaCompleted={currentPou?.tinanaCompleted}
                    whanauCompleted={currentPou?.whanauCompleted}
                    size={140}
                  />
                </CardContent>
              </Card>

              {/* Goals */}
              {currentPou && (
                <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-brand flex items-center gap-2">
                      <Target className="w-4 h-4" /> Daily Taha Goals
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {['wairua', 'hinengaro', 'tinana', 'whanau'].map((pillar) => {
                      const title = currentPou[`${pillar}GoalTitle`];
                      return title ? (
                        <div key={pillar} className="flex items-center justify-between p-2 bg-[#F9F9F7] rounded-xl">
                          <div>
                            <p className="text-xs text-[#8FA39B] capitalize">{pillar}</p>
                            <p className="text-sm text-[#051F17]">{title}</p>
                          </div>
                        </div>
                      ) : null;
                    })}
                    {!currentPou.wairuaGoalTitle && !currentPou.hinengaroGoalTitle && 
                     !currentPou.tinanaGoalTitle && !currentPou.whanauGoalTitle && (
                      <p className="text-center text-sm text-[#8FA39B] py-4">No goals set yet</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="journals">
              <ScrollArea className="h-[60vh]">
                <div className="space-y-3">
                  {memberData?.journals?.length === 0 ? (
                    <p className="text-center text-sm text-[#8FA39B] py-8">No journal entries yet</p>
                  ) : (
                    memberData?.journals?.map((entry) => (
                      <Card key={entry.id} className="bg-white border border-[#0B3D2E]/5 rounded-2xl">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium text-[#051F17]">{entry.date}</p>
                            <Badge className="bg-[#0B3D2E]/10 text-[#0B3D2E]">{entry.mood}</Badge>
                          </div>
                          {entry.gratitude && (
                            <p className="text-sm text-[#4A5D56]">{entry.gratitude}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="nutrition">
              <ScrollArea className="h-[60vh]">
                <div className="space-y-3">
                  {memberData?.nutrition?.length === 0 ? (
                    <p className="text-center text-sm text-[#8FA39B] py-8">No nutrition entries yet</p>
                  ) : (
                    memberData?.nutrition?.map((entry) => (
                      <Card key={entry.id} className="bg-white border border-[#0B3D2E]/5 rounded-2xl">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium text-[#051F17]">{entry.mealType}</p>
                            <p className="text-xs text-[#8FA39B]">{entry.date}</p>
                          </div>
                          <p className="text-sm text-[#4A5D56]">{entry.calories} cal • {entry.protein}g protein</p>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="activities">
              <ScrollArea className="h-[60vh]">
                <div className="space-y-3">
                  {memberData?.activities?.length === 0 ? (
                    <p className="text-center text-sm text-[#8FA39B] py-8">No activities logged yet</p>
                  ) : (
                    memberData?.activities?.map((entry) => (
                      <Card key={entry.id} className="bg-white border border-[#0B3D2E]/5 rounded-2xl">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-[#051F17]">{entry.activityType}</p>
                              <p className="text-xs text-[#4A5D56]">
                                {entry.durationMinutes} mins • {entry.intensity} intensity
                              </p>
                            </div>
                            <p className="text-xs text-[#8FA39B]">{entry.date}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
            
            <TabsContent value="badges">
              <div className="space-y-4">
                {/* Current badges */}
                <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-brand">Earned Badges</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {memberBadges.length === 0 ? (
                      <p className="text-center text-sm text-[#8FA39B] py-4">No badges earned yet</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {memberBadges.map((badge) => (
                          <div 
                            key={badge.id}
                            className="flex items-center gap-2 px-3 py-2 bg-[#D4A84B]/10 text-[#D4A84B] rounded-full"
                          >
                            <Award className="w-4 h-4" />
                            <span className="font-medium text-sm">{badge.badgeName}</span>
                            <button 
                              onClick={() => revokeBadge(badge.id)}
                              className="text-[#B85C4C] hover:text-[#B85C4C]/80"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Award new badge */}
                <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-brand">Award Badge</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {badges.filter(b => !memberBadges.some(mb => mb.badgeId === b.id)).map((badge) => (
                        <Button
                          key={badge.id}
                          variant="outline"
                          size="sm"
                          onClick={() => awardBadge(selectedMember.id, badge.id)}
                          className="border-[#0B3D2E]/20"
                        >
                          <Award className="w-3 h-3 mr-1" /> {badge.name}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    );
  }

  // Main Dashboard View with Tabs
  return (
    <div className="p-4 pb-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-brand text-[#0B3D2E] flex items-center gap-2">
          <Users className="w-6 h-6" /> Coach Dashboard
        </h1>
        <p className="text-sm text-[#4A5D56]">Manage your whānau</p>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled Posts</TabsTrigger>
        </TabsList>
        
        {/* Members Tab */}
        <TabsContent value="members">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8FA39B]" />
            <Input
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 bg-[#F9F9F7] border-0 rounded-xl"
              data-testid="member-search"
            />
          </div>

          {/* Members List */}
          <div className="space-y-3">
            {filteredMembers.length === 0 ? (
              <p className="text-center text-sm text-[#8FA39B] py-8">No members found</p>
            ) : (
              filteredMembers.map((member) => (
                <Card 
                  key={member.id}
                  className="bg-white border border-[#0B3D2E]/5 rounded-2xl cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => selectMember(member)}
                  data-testid={`member-card-${member.id}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <Avatar className="w-12 h-12 bg-[#0B3D2E]">
                      {member.profilePhoto ? (
                        <AvatarImage src={member.profilePhoto} />
                      ) : null}
                      <AvatarFallback className="bg-[#0B3D2E] text-white">
                        {getInitials(member.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#051F17] truncate">{member.name}</p>
                      <p className="text-xs text-[#4A5D56] truncate">{member.email}</p>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      <Badge className={getTierColor(member.tier)}>
                        {member.tier || 'free'}
                      </Badge>
                      <Badge className={getRoleColor(member.role)}>
                        {member.role || 'user'}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => openEditRole(member, e)}
                      className="text-[#4A5D56]"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
        
        {/* Scheduled Posts Tab */}
        <TabsContent value="scheduled">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-brand text-lg">Scheduled Posts</h2>
            <Button 
              onClick={() => setShowNewPostDialog(true)}
              className="bg-[#0B3D2E] text-white rounded-full"
            >
              <Plus className="w-4 h-4 mr-1" /> New Post
            </Button>
          </div>
          
          {loadingScheduled ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-[#0B3D2E]/20 border-t-[#0B3D2E] rounded-full animate-spin"></div>
            </div>
          ) : scheduledPosts.length === 0 ? (
            <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl">
              <CardContent className="p-8 text-center">
                <Clock className="w-12 h-12 mx-auto text-[#0B3D2E]/20 mb-4" />
                <h3 className="text-lg font-semibold text-[#051F17] mb-2">No scheduled posts</h3>
                <p className="text-sm text-[#4A5D56]">Create a post to schedule it for later</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {scheduledPosts.map((post) => (
                <Card key={post.id} className="bg-white border border-[#0B3D2E]/5 rounded-2xl">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className={post.status === 'draft' ? 'bg-[#8FA39B]' : 'bg-[#5B8BA0]'}>
                          {post.status}
                        </Badge>
                        {post.scheduledFor && (
                          <span className="text-xs text-[#4A5D56] flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(post.scheduledFor)}
                          </span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteScheduledPost(post.id)}
                        className="text-[#B85C4C]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-[#051F17] line-clamp-2">{post.text}</p>
                    {post.imageUrl && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-[#8FA39B]">
                        {post.mediaType === 'video' ? <Video className="w-3 h-3" /> : <Image className="w-3 h-3" />}
                        Media attached
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Role Dialog */}
      <Dialog open={showEditRoleDialog} onOpenChange={setShowEditRoleDialog}>
        <DialogContent className="max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="font-brand">Edit Access - {editingMember?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Membership Tier</Label>
              <Select value={newTier} onValueChange={setNewTier}>
                <SelectTrigger className="h-12 bg-[#F9F9F7] border-0 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="member">TRIBE Member</SelectItem>
                  <SelectItem value="challenger">TRIBE Challenger</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="h-12 bg-[#F9F9F7] border-0 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="coach">Coach</SelectItem>
                  {user?.role === 'admin' && (
                    <SelectItem value="admin">Admin</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              onClick={saveRoleChange}
              disabled={savingRole}
              className="w-full h-12 bg-[#0B3D2E] text-white rounded-full"
            >
              {savingRole ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* New Scheduled Post Dialog */}
      <Dialog open={showNewPostDialog} onOpenChange={setShowNewPostDialog}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="font-brand">Create Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Post Content</Label>
              <Textarea
                placeholder="Share with the whānau..."
                value={newPost.text}
                onChange={(e) => setNewPost({...newPost, text: e.target.value})}
                className="min-h-[100px] bg-[#F9F9F7] border-0 rounded-xl resize-none"
              />
            </div>
            
            <div>
              <Label>Media URL (optional)</Label>
              <Input
                placeholder="https://..."
                value={newPost.imageUrl}
                onChange={(e) => setNewPost({...newPost, imageUrl: e.target.value, mediaType: e.target.value ? 'image' : null})}
                className="h-12 bg-[#F9F9F7] border-0 rounded-xl"
              />
            </div>
            
            <div>
              <Label>Schedule for (optional - NZ timezone)</Label>
              <Input
                type="datetime-local"
                value={newPost.scheduledFor}
                onChange={(e) => setNewPost({...newPost, scheduledFor: e.target.value})}
                className="h-12 bg-[#F9F9F7] border-0 rounded-xl"
              />
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={() => { setNewPost({...newPost, scheduledFor: ""}); createScheduledPost(); }}
                disabled={creatingPost}
                variant="outline"
                className="flex-1 h-12 border-[#0B3D2E]/30 rounded-full"
              >
                Post Now
              </Button>
              <Button 
                onClick={createScheduledPost}
                disabled={creatingPost || !newPost.scheduledFor}
                className="flex-1 h-12 bg-[#0B3D2E] text-white rounded-full"
              >
                {creatingPost ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <><Clock className="w-4 h-4 mr-1" /> Schedule</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
